import { AbsoluteTimeRange } from '@chronosphereio/core';
import {
  Labels,
  SeriesConfig,
  TimeSeriesChartVisualOptions,
  TimeSeriesValueTuple,
} from '@chronosphereio/viz-framework-core';
import { TruncationBandTotals } from '@/core';
import { HeatMapTimeSeriesData } from '../heatmap-data';
import { SupportedData } from '../query';
import {
  TIME_SERIES_DATASET_DIMENSIONS,
  TimeScale,
  TimeSeries,
  TimeSeriesData,
  TimeSeriesDataset,
} from '../time-series-data';
import { gcd } from './mathjs';

/**
 * Since stepMs is optional in TimeSeriesData and TimeScale interfaces, we need a suitable fallback
 * for most use cases that balances performance and accuracy. Since logs requires higher fidelity
 * than metrics, 1s is chosen (compared to 15s in our prometheus-plugin, suggested step logic).
 */
export const MIN_STEP_FALLBACK_MS = 1000;

/**
 * Given a common time scale (see `getCommonTimeScale`), generates an array of
 * timestamp values in ms for the x axis of a graph.
 */
export function getXValues(timeScale: TimeScale): number[] {
  const stepMs = timeScale.stepMs ?? MIN_STEP_FALLBACK_MS;
  const xValues: number[] = [];
  let timestamp = timeScale.startMs;
  while (timestamp <= timeScale.endMs) {
    xValues.push(timestamp);
    timestamp += stepMs;
  }
  return xValues;
}

export type TimeSeriesTotals = Map<number, number>;

export type GetTimeSeriesValuesArgs = {
  series: TimeSeries;
  timeScale: TimeScale;
  /**
   * Visual options like null_behavior 'NullAsZero' impact processing (ex: fill in zero instead of nulls).
   */
  visual?: TimeSeriesChartVisualOptions;
  /**
   * Total of each series per timestamp, used to calculate normalized values for percent stacking.
   * Optional since totals do not need to be computed unless `visual.stack: 'Percent'` is set.
   */
  totalValuePerTimestamp?: TimeSeriesTotals;
  /**
   * Whether to include series calculations, depends on which panel settings are used.
   * Ex: calcs.totalValue is precomputed (for better performance) when visual.stack is set and used to sort series.
   */
  includeCalcs?: boolean;
  truncatedSeriesTotals?: TruncationBandTotals;
};

/**
 * Given a TimeSeries from a query and a common time scale (see `getCommonTimeScale`),
 * processes the time series values, filling in any timestamps that are missing
 * from the time series data with `null` values.
 * It also returns all the data points that are surrounded by null values (nonconnectedDataPoints).
 * e.g. [2, null, 3, null, null, 4 , 5] In this case, 2 and 3 are both nonconnected.
 *
 * @param series
 * @param timeScale
 * @param display It defaults to `line`
 *   If `line` is selected, it will add null values to the end of the series if necessary, including the last timestamp.
 *   If `bar` is selected, it will add null values to the end of the series if necessary, but excluding the last timestamp.
 */
export function getTimeSeriesValues({
  series,
  timeScale,
  visual,
  totalValuePerTimestamp,
  includeCalcs,
  truncatedSeriesTotals,
}: GetTimeSeriesValuesArgs) {
  let timestamp = timeScale.startMs;

  const values = series.values;
  const processedValues: TimeSeriesValueTuple[] = [];
  const nonconnectedDataPoints: TimeSeriesValueTuple[] = [];

  let seriesTotal = 0;

  // Gives control over whether nulls should be connected, show as zero, etc.
  const nullBehavior = visual?.null_behavior;
  const nullValue = nullBehavior === 'NullAsZero' ? 0 : null;

  const isPercentStacked = visual?.stack === 'Percent' && totalValuePerTimestamp !== undefined;

  // When all series return stepMs as undefined, assume query plugin has already processed the values.
  // This prevents 'No data' from being shown unnecessarily (see PR #60054).
  if (timeScale.stepMs === undefined) {
    return {
      values: series.values,
      nonconnectedDataPoints: [],
    };
  }

  for (let i = 0; i < values.length; i++) {
    const valueTuple = values[i];

    if (valueTuple === undefined) {
      throw new Error('value tuple is undefined');
    }

    // Fill in values up to the current series value timestamp with nulls
    while (timestamp < valueTuple[0]) {
      processedValues.push([timestamp, nullValue]);
      timestamp += timeScale.stepMs;
    }

    // Now add the current value since timestamp should match, render Infinity as null so chart does not go haywire.
    // Convert to percentage if totals are provided.
    let value = valueTuple[1] === Infinity ? nullValue : valueTuple[1];
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (includeCalcs) {
        // Total of all values for this series
        seriesTotal += value;
      }
      if (isPercentStacked) {
        // Total of all series in the stack at this timestamp
        const total = totalValuePerTimestamp.get(timestamp);
        value = calculateValueAsPercentage({ value, total, fallbackValue: nullValue });
      }
      // Account for multiple queries hitting truncation_strategy limit
      if (truncatedSeriesTotals !== undefined && value !== null) {
        const map = truncatedSeriesTotals.maps[truncatedSeriesTotals.band];
        const lastTruncatedValue = map.get(timestamp);
        if (truncatedSeriesTotals.band === 'upper') {
          // Take higher truncated value at timestamp for top confidence band
          if (lastTruncatedValue === undefined || value > lastTruncatedValue) {
            map.set(timestamp, value);
          }
        } else if (truncatedSeriesTotals.band === 'lower') {
          // Take lower truncated value at timestamp for bottom confidence band
          if (lastTruncatedValue === undefined || value < lastTruncatedValue) {
            map.set(timestamp, value);
          }
        } else {
          // 'stack' case to sum truncated values in synthetic 'Truncated series totals' series
          map.set(timestamp, (lastTruncatedValue ?? 0) + value);
        }
      }
    }
    processedValues.push([timestamp, value]);

    // Check if value is connected to other data points
    const prevTuple = i > 0 ? values[i - 1] : null;
    const nextTuple = i < values.length - 1 ? values[i + 1] : null;
    const prevValue = prevTuple ? prevTuple[1] : null;
    const nextValue = nextTuple ? nextTuple[1] : null;
    if (isDatapointNotConnected(valueTuple[1], prevValue, nextValue)) {
      nonconnectedDataPoints.push([timestamp, valueTuple[1]]);
    }

    timestamp += timeScale.stepMs;
  }

  // Add null values at the end of the series if necessary
  while (timestamp <= timeScale.endMs) {
    processedValues.push([timestamp, nullValue]);
    timestamp += timeScale.stepMs;
  }

  if (includeCalcs) {
    return {
      values: processedValues,
      nonconnectedDataPoints,
      calcs: { totalValue: seriesTotal, truncatedSeriesTotals },
    };
  }

  return { values: processedValues, nonconnectedDataPoints };
}

/**
 * Calculates the percentage of a value relative to a total, handling edge cases.
 * Returns the normalized value or a specified fallback value.
 */
export function calculateValueAsPercentage({
  value,
  total,
  fallbackValue = null,
}: {
  value?: number;
  total?: number;
  fallbackValue?: number | null;
}) {
  if (value == null || total == null) return fallbackValue;
  if (total === 0) return value === 0 ? 0 : fallbackValue;
  return (value / total) * 100;
}

/**
 * [DEPRECATED] Used for legacy LineChartBase 'category' axis approach.
 * Given a TimeSeries from a query and a common time scale (see `getCommonTimeScale`),
 * gets the values for the y axis of a graph, filling in any timestamps that are
 * missing from the time series data with `null` values.
 */
export function getYValues(series: TimeSeries, timeScale: TimeScale): Array<number | null> {
  let timestamp = timeScale.startMs;
  const stepMs = timeScale.stepMs ?? MIN_STEP_FALLBACK_MS;
  const yValues: Array<number | null> = [];
  for (const valueTuple of series.values) {
    // Fill in values up to the current series value timestamp with nulls
    while (timestamp < valueTuple[0]) {
      yValues.push(null);
      timestamp += stepMs;
    }

    // Now add the current value since timestamp should match
    yValues.push(valueTuple[1]);
    timestamp += stepMs;
  }

  // Add null values at the end of the series if necessary
  while (timestamp <= timeScale.endMs) {
    yValues.push(null);
    timestamp += stepMs;
  }

  return yValues;
}

/**
 * Given a list of running queries, calculates a common time scale for use on
 * the x axis (i.e. start/end dates and a step that is divisible into all of
 * the queries' steps).
 */
export function getCommonTimeScale(
  seriesData: Array<TimeSeriesData | Pick<TimeSeries, 'values'> | undefined>
): TimeScale | undefined {
  let timeRange: AbsoluteTimeRange | undefined = undefined;
  const steps: number[] = [];

  for (const data of seriesData) {
    if (data && 'timeRange' in data) {
      if (data === undefined || data.timeRange === undefined) continue;

      // If we don't have an overall time range yet, just start with this one
      if (timeRange === undefined) {
        timeRange = data.timeRange;
        if (data.stepMs !== undefined) {
          steps.push(data.stepMs);
        }
        continue;
      }

      // Otherwise, see if this query has a start or end outside of the current
      // time range
      if (data.timeRange.start < timeRange.start) {
        timeRange = { ...timeRange, start: data.timeRange.start };
      }
      if (data.timeRange.end > timeRange.end) {
        timeRange = { ...timeRange, end: data.timeRange.end };
      }

      if (data.stepMs === undefined) {
        continue;
      }

      // Keep track of query steps so we can calculate a common one for the graph
      steps.push(data.stepMs);
    } else if (data && 'values' in data) {
      for (let i = 0; i < data.values.length; i++) {
        const values = data.values[i];
        if (values === undefined) {
          continue;
        }

        const [timestamp] = values;
        const start = new Date(timestamp);
        const end = new Date(timestamp);

        if (timeRange === undefined) {
          timeRange = {
            start,
            end,
          };
          continue;
        }

        if (start < timeRange.start) {
          timeRange.start = start;
        }

        if (end > timeRange.end) {
          timeRange.end = end;
        }
      }

      if (timeRange === undefined) return undefined;

      const startMs = timeRange.start.valueOf();
      const endMs = timeRange.end.valueOf();
      const rangeMs = endMs - startMs;

      return { startMs, endMs, rangeMs, stepMs: MIN_STEP_FALLBACK_MS };
    }
  }

  if (timeRange === undefined) return undefined;

  // Use the greatest common divisor of all step values as the overall step
  // for the x axis (or if only one query, just use that query's step value)
  let stepMs: number | undefined;
  if (steps.length === 1) {
    stepMs = steps[0] as number;
  } else if (steps.length > 1) {
    const calculatedStepMs = gcd(...steps);
    stepMs = calculatedStepMs < MIN_STEP_FALLBACK_MS ? MIN_STEP_FALLBACK_MS : calculatedStepMs;
  }

  const startMs = timeRange.start.valueOf();
  const endMs = timeRange.end.valueOf();
  const rangeMs = endMs - startMs;

  return { startMs, endMs, stepMs, rangeMs };
}

/**
 * Used to ensure sparse data is rendered by checking if current datapoint is connected to for prev or next.
 */
export function isDatapointNotConnected(
  currentValue: number | null,
  prevValue: number | null,
  nextValue: number | null
) {
  if (currentValue === null) return false;
  const isPrevDisconnected = prevValue === null;
  const isNextDisconnected = nextValue === null;
  return isPrevDisconnected && isNextDisconnected;
}

export type GenerateTimeSeriesDatasetOpts = Omit<TimeSeriesDataset, 'dimensions' | 'source'> & {
  queryResultIndex: number;
  compareToSeriesId?: string;
  isComparedSeries?: boolean;
  labels?: Labels;
  config?: SeriesConfig;
};
/**
 * Converts time series data into a dataset format that is used for additional
 * data processing and rendering of the chart.
 */
export const generateTimeSeriesDataset = (
  timeSeriesData: TimeSeriesValueTuple[],
  {
    name,
    id,
    formattedName,
    labels,
    config,
    queryResultIndex,
    compareToSeriesId,
    calcs,
    nonconnectedDataPoints,
  }: GenerateTimeSeriesDatasetOpts
): TimeSeriesDataset => {
  // TODO: consider moving more of the code that needs to iterate over all the time series
  // here or to getTimeSeriesValues (e.g. min/max calculations) and provide necessary results via
  // some sort of metadata in the response, so we can limit the number of times
  // we iterate a massive list of data.
  return {
    name,
    id,
    ...(formattedName !== undefined ? { formattedName } : {}),
    dimensions: TIME_SERIES_DATASET_DIMENSIONS,
    source: timeSeriesData,
    queryResultIndex,
    config,
    labels,
    compareToSeriesId,
    calcs,
    nonconnectedDataPoints,
  };
};

export function isTimeSeriesData(data: SupportedData): data is TimeSeriesData {
  return 'series' in data;
}

export function isHeatMapTimeSeriesData(data: SupportedData): data is HeatMapTimeSeriesData {
  return 'heatmap' in data;
}
